/**
 * Generic Prompt Builder Utility
 * Creates structured prompts for generic brand/company analysis using the simple-search API
 */

import { GenericPromptParams, TimePeriod, PromptMetadata } from '../models/generic-models';

// Re-export for backward compatibility
export type { GenericPromptParams, TimePeriod, PromptMetadata };

export class GenericPromptBuilder {

  /**
   * Clean brand names by removing (YOU) suffix and normalizing
   * @param brandName The brand name to clean
   * @returns Cleaned brand name
   */
  private static cleanBrandName(brandName: string): string {
    return brandName.replace(/\s*\(YOU\)\s*/g, '').trim();
  }

  /**
   * Clean array of brand names
   * @param brands Array of brand names to clean
   * @returns Array of cleaned brand names
   */
  private static cleanBrandNames(brands: string[]): string[] {
    return brands.map(brand => this.cleanBrandName(brand));
  }

  /**
   * Build a comprehensive prompt for generic overview analysis
   * Returns a structured prompt that will generate the expected response format
   */
  static buildGenericOverviewPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global', category, companySize, marketCap, businessModel, specialization, useCase = 'brand_monitoring' } = params;

    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');
    const countryText = country === 'Global' ? 'globally' : `in ${country}`;
    const categoryText = category ? ` in the ${category} category` : '';

    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert brand analyst conducting comprehensive market research for the ${industry} industry${categoryText}.

Analyze these brands${countryText}: ${brandsText}

For each brand, provide detailed analysis including:
1. Brand reputation and market perception
2. Market presence and competitive positioning
3. Customer satisfaction metrics
4. Innovation score and technological advancement
5. Financial performance indicators
6. Industry influence and thought leadership

Provide your analysis in this exact JSON format:

{
  "composite_score": {
    "value": 75.0,
    "label": "Good",
    "color": "#ff9800",
    "brand": "${cleanedBrands[0]}"
  },
  "competitor_analysis": {
    "title": "${cleanedBrands[0]} vs Competitors Analysis",
    "subtitle": "${cleanedBrands[0]} vs Competitors",
    "x_axis": [
      "Brand Reputation",
      "Market Presence",
      "Customer Satisfaction", 
      "Innovation Score",
      "Financial Performance",
      "Industry Influence"
    ],
    "y_axis": "Score",
    "chart_type": "combo",
    "series": [
      {
        "name": "${cleanedBrands[0]}",
        "data": [85, 78, 82, 88, 75, 80],
        "type": "bar",
        "color": "#ff9800"
      }${cleanedBrands.length > 1 ? cleanedBrands.slice(1).map((brand, index) => `,
      {
        "name": "${brand}",
        "data": [75, 72, 78, 85, 70, 82],
        "type": "line",
        "color": "${this.getColorForIndex(index + 1)}"
      }`).join('') : ''}
    ]
  },
  "performance_table": [
    ${cleanedBrands.map((brand, index) => `{
      "brand": "${brand}",
      "is_your_brand": ${index === 0},
      "brand_reputation": 80,
      "market_presence": 75,
      "customer_satisfaction": 82,
      "innovation_score": 88,
      "financial_performance": 79,
      "industry_influence": 85,
      "market_share": "10-20",
      "ranking": "Top 50"
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "references": {
    "methodology": "<div class=\\"methodology-content\\" style=\\"line-height: 1.6; font-family: Arial, sans-serif;\\">Title: Methodology for Calculating ${industry} Competitor Scores<br><br><strong>Abstract:</strong> This report outlines the methodology employed to calculate ${industry} competitor scores, a comprehensive evaluation metric designed to assess the competitive standing of ${industry} institutions. The scoring system integrates several key components: brand reputation, market presence, customer satisfaction, innovation, financial performance, and industry influence.<br><br><strong>1. **Brand Reputation**: The assessment of brand reputation is conducted through market research, customer feedback, and industry surveys. Metrics include brand recognition, customer loyalty, and market perception.<br><br><strong>2. **Market Presence**: Market presence is quantified by analyzing market share, geographic reach, customer base, and competitive positioning within the ${industry} sector.<br><br><strong>3. **Customer Satisfaction**: Customer satisfaction metrics are derived from surveys, reviews, and retention rates to ensure reliability and validity of customer experience indicators.<br><br><strong>4. **Innovation Score**: Innovation assessment involves examining R&D investments, patent filings, new product launches, and technological advancement within the ${industry} landscape.<br><br><strong>5. **Financial Performance**: Financial metrics include revenue growth, profitability, market capitalization, and investment returns to gauge financial stability and growth potential.<br><br><strong>6. **Industry Influence**: Industry influence is measured through thought leadership, partnerships, regulatory involvement, and contribution to industry standards and best practices.<br><br><strong>Conclusion:</strong> The comprehensive analysis of these components provides a holistic view of an institution's competitive standing in the ${industry} sector. This methodology ensures a balanced evaluation, reflecting both qualitative and quantitative aspects of institutional performance.</div>",
    "sources": [],
    "last_updated": "2025-01-01T00:00:00.000Z"
  },
  "metadata": {
    "response_time": 10.0,
    "brands_analyzed": ${cleanedBrands.length},
    "data_source": "LLM + Web Search",
    "industry": "${industry}",
    "your_brand": "${cleanedBrands[0]}",
    "competitors": ${JSON.stringify(cleanedBrands.slice(1))},
    "timestamp": "2025-01-01T00:00:00.000Z"
  }
}

IMPORTANT: 
- Use realistic scores (0-100) based on your analysis
- Replace placeholder values with actual analysis results
- Ensure valid JSON syntax
- No additional text outside the JSON object
- Focus on ${countryText} market conditions and ${industry} industry trends`;
  }


  /**
   * Build a prompt for AI Citations analysis
   */
  static buildAICitationsPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global' } = params;
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');
    const primaryBrand = cleanedBrands[0];
    const competitorBrands = cleanedBrands.slice(1);

    // CLEAN, SIMPLE PROMPT (No fake citation templates)
    return `Analyze AI citations and visibility for ${brandsText}${country === 'Global' ? ' globally' : ` in ${country}`} within the ${industry} industry.

Focus on:
1. Citation frequency and share of voice
2. Authority weight and relevance scores
3. AI readability and content quality
4. External mentions and diversity index
5. Persistence score and long-term visibility

CRITICAL RULES:
- Analyze ALL brands: ${brandsText}
- Include sources that specifically mention any of these brands in their content
- Focus on official brand sites, industry news, tech publications, and business sources
- Ensure all citations are directly related to each brand's AI/technology activities
- Provide comparative analysis between ${primaryBrand} and competitors: ${competitorBrands.join(', ')}

Return JSON in this format:
{
  "brands": [
    {
      "name": "${primaryBrand}",
      "is_your_brand": true,
      "citation_frequency": <number 0-100>,
      "citation_share_of_voice": <number 0-100>,
      "authority_weight": <number 0-100>,
      "relevance_score": <number 0-100>,
      "ai_readability": <number 0-100>,
      "external_mentions": <number 0-100>,
      "diversity_index": <number 0-100>,
      "persistence_score": <number 0-100>
    }${competitorBrands.map(brand => `,
    {
      "name": "${brand}",
      "is_your_brand": false,
      "citation_frequency": <number 0-100>,
      "citation_share_of_voice": <number 0-100>,
      "authority_weight": <number 0-100>,
      "relevance_score": <number 0-100>,
      "ai_readability": <number 0-100>,
      "external_mentions": <number 0-100>,
      "diversity_index": <number 0-100>,
      "persistence_score": <number 0-100>
    }`).join('')}
  ],
  "context": {
    "title": "AI Citation Analysis",
    "insights": [
      "<3 key insights comparing AI visibility across ${brandsText}>"
    ],
    "citations": []
  },
  "timestamp": "2025-01-01T00:00:00.000Z",
  "model_meta": {
    "model_used": "gpt-4o",
    "search_engines": ["Tavily"],
    "data_freshness": "live",
    "processing_time": 0,
    "web_search_used": true,
    "citation_count": 0
  }
}

IMPORTANT: Do NOT generate fake citations. Backend will inject real ones from web search.`;
  }

  /**
   * Build a prompt for Category Analysis
   */
  static buildCategoryAnalysisPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global' } = params;
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');

    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert category analyst specializing in ${industry} market segmentation and analysis.

Analyze category performance for these brands${country === 'Global' ? ' globally' : ` in ${country}`}: ${brandsText}

IMPORTANT: Generate dynamic categories that are specifically relevant to ${cleanedBrands[0]} and the ${industry} industry. Do NOT use generic education categories like "Arts, Humanities & Social Sciences" or "Business & Management". Instead, create categories that make sense for ${cleanedBrands[0]}'s business model, products, and market presence.

For example, if analyzing BMW in automotive industry, use categories like:
- "Luxury Vehicle Market"
- "Electric & Hybrid Vehicles" 
- "Autonomous Driving Technology"
- "Performance & Sports Cars"
- "Commercial Vehicles"
- "Digital Services & Connectivity"
- "Sustainability & Green Technology"
- "Customer Experience & Services"
- "Innovation & R&D"

Focus on:
1. Category demand and market trends specific to ${cleanedBrands[0]}'s business
2. Performance metrics relevant to ${cleanedBrands[0]}'s product/service categories
3. Competitive positioning within ${cleanedBrands[0]}'s actual market categories
4. Market share and growth potential in ${cleanedBrands[0]}'s core business areas
5. Category-specific strengths and opportunities for ${cleanedBrands[0]}

Provide your analysis in this exact JSON format:

{
  "brands": [
    ${cleanedBrands.map((brand, index) => `{
      "name": "${brand}",
      "composite_score": 80,
      "category_scores": [85, 78, 82, 88, 75, 80],
      "is_your_brand": ${index === 0}
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "chart_data": {
    "composite_score": {
      "value": 80,
      "label": "Overall Composite Score",
      "color": "blue",
      "brand": "${cleanedBrands[0]}"
    },
    "ai_category_insights": {
      "title": "AI Category Insights by Business Area",
      "type": "combo-chart-v2",
      "x_axis": [
        "Luxury Vehicle Market",
        "Electric & Hybrid Vehicles",
        "Autonomous Driving Technology",
        "Performance & Sports Cars",
        "Commercial Vehicles",
        "Digital Services & Connectivity",
        "Sustainability & Green Technology",
        "Customer Experience & Services",
        "Innovation & R&D"
      ],
      "series": [
        {
          "name": "${cleanedBrands[0]}",
          "type": "line",
          "data": [85, 78, 82, 88, 75, 80, 87, 84, 86]
        }
      ]
    },
    "ai_table_summary": {
      "title": "AI Visibility Table by Category",
      "headers": [
        "Category",
        "Score"
      ],
      "rows": [
        ["Luxury Vehicle Market", 85],
        ["Electric & Hybrid Vehicles", 90],
        ["Autonomous Driving Technology", 88],
        ["Performance & Sports Cars", 82],
        ["Commercial Vehicles", 78],
        ["Digital Services & Connectivity", 87],
        ["Sustainability & Green Technology", 84],
        ["Customer Experience & Services", 86],
        ["Innovation & R&D", 92]
      ]
    },
    "category_demand": {
      "title": "Category Performance (High-Demand Areas)",
      "type": "combo-chart-v2",
      "series": [
        {
          "name": "Electric & Hybrid Vehicles",
          "value": 90,
          "rate": 2.5,
          "forecast": 93
        },
        {
          "name": "Autonomous Driving Technology",
          "value": 88,
          "rate": 3.2,
          "forecast": 91
        },
        {
          "name": "Digital Services & Connectivity",
          "value": 87,
          "rate": 2.8,
          "forecast": 89
        }
      ]
    },
    "subject_demand_analysis": {
      "title": "Subject Demand Analysis",
      "type": "combo-chart-v2",
      "series": [
        {
          "name": "${cleanedBrands[0]}",
          "data": {
            "Market Share": 82,
            "Brand Reputation": 85,
            "Customer Satisfaction": 83,
            "Innovation Index": 88,
            "Financial Performance": 80,
            "Industry Leadership": 86,
            "AI mention": 84,
            "AI Sentiment": 87,
            "AI cited domains": 81,
            "AI page Index": 83,
            "Product Range": 85,
            "Global Presence": 89,
            "Market Growth": "+12%"
          }
        }
      ]
    },
    "brand_info": {
      "brand_reputation": 85,
      "market_dominance": 82,
      "customer_loyalty": 83,
      "innovation_score": 88,
      "global_presence": 89,
      "financial_strength": 80,
      "sustainability_rating": 84,
      "digital_transformation": 87,
      "prediction_next_5y": "${cleanedBrands[0]} is expected to maintain its leadership position in the ${industry} industry, with continued focus on innovation, digital transformation, and sustainable growth strategies."
    },
    "recommendations": {
      "strengths": [
        "Strong innovation and technology integration in ${industry}",
        "Robust global market presence and brand recognition",
        "Excellent customer satisfaction and brand loyalty"
      ],
      "weaknesses": [
        "Opportunity to enhance sustainability initiatives",
        "Potential to expand digital transformation efforts",
        "Room for improvement in emerging market penetration"
      ]
    },
    "citations": [
      {
        "source": "DuckDuckGo",
        "url": "https://duckduckgo.com/?q=Brand%20reputation%20and%20performance%20of%20${cleanedBrands[0]}%20in%20${industry}",
        "title": "Search Results for: Brand reputation and performance of ${cleanedBrands[0]} in ${industry}",
        "snippet": "Search results for Brand reputation and performance of ${cleanedBrands[0]} in ${industry} from DuckDuckGo search engine with comprehensive information and resources.",
        "confidence": 0.7
      }
    ]
  },
  "context": {
    "analysis_type": "category_analysis",
    "total_brands": ${cleanedBrands.length},
    "category": "${industry}",
    "region": "${country}",
    "analysis_timestamp": "2025-01-01T00:00:00.000Z"
  },
  "timestamp": "2025-01-01T00:00:00.000Z",
  "model_meta": {
    "model_version": "category_analysis_v2_ai",
    "processing_time": 10.0,
    "data_sources": [
      "ai_analysis",
      "web_search"
    ],
    "web_search_used": true,
    "brands_analyzed": ${cleanedBrands.length}
  }
}

IMPORTANT: 
1. Generate dynamic categories that are specifically relevant to ${cleanedBrands[0]}'s business model and the ${industry} industry
2. Replace the example categories (Luxury Vehicle Market, Electric & Hybrid Vehicles, etc.) with categories that make sense for ${cleanedBrands[0]}
3. Ensure the number of data points in the "data" array matches the number of categories in the "x_axis" array
4. Provide realistic category analysis based on current market trends and brand performance
5. Focus on actual business categories that ${cleanedBrands[0]} operates in, not generic education or academic categories`;
  }

  /**
   * Build a prompt for Sentiment Tracking analysis
   */
  static buildSentimentTrackingPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global' } = params;
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');

    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert sentiment analyst specializing in brand reputation and customer sentiment analysis in the ${industry} industry.

Analyze sentiment and reputation for these brands${country === 'Global' ? ' globally' : ` in ${country}`}: ${brandsText}

Focus on:
1. Overall sentiment scores across platforms
2. Social media sentiment analysis
3. Customer satisfaction metrics
4. News sentiment and media coverage
5. Service quality and customer experience tracking
6. Financial sentiment and value proposition perception

Provide your analysis in this exact JSON format:

{
  "brands": [
    ${cleanedBrands.map((brand, index) => `{
      "name": "${brand}",
      "sentiment_score": 82,
      "social_media_sentiment": 80,
      "customer_satisfaction": 85,
      "news_sentiment": 78,
      "overall_reputation": 83,
      "platform_sentiment": {
        "twitter": 81,
        "reddit": 79,
        "customer_reviews": 86,
        "news_articles": 78
      },
      "sentiment_tracking": {
        "service_quality": 84,
        "customer_experience": 86,
        "financial": 82,
        "value_proposition": 80,
        "trust_safety": 85
      },
      "key_insights": [
        "Strong brand reputation in ${industry.toLowerCase()} market",
        "Positive customer feedback on service experience",
        "Recent innovations are well-received"
      ]
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "composite_score": {
    "value": 82,
    "label": "Sentiment Score",
    "color": "#1976d2",
    "brand": "${cleanedBrands[0]}"
  },
  "competitor_analysis": {
    "title": "Brand Sentiment Comparison",
    "subtitle": "Sentiment Analysis Across Brands",
    "series": [
      ${cleanedBrands.map((brand, index) => `{
        "name": "${brand}",
        "data": [82, 80, 85, 78, 83, 84],
        "type": "bar",
        "color": "#1976d2"
      }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n      ')}
    ],
    "x_axis": [
      "Sentiment Score",
      "Social Media",
      "Customer Satisfaction",
      "News Sentiment",
      "Overall Reputation"
    ],
    "y_axis": "Score",
    "chart_type": "combo"
  },
  "performance_table": [
    ${cleanedBrands.map((brand, index) => `{
      "brand": "${brand}",
      "is_your_brand": ${index === 0},
      "sentiment_score": 82,
      "social_media_sentiment": 80,
      "customer_satisfaction": 85,
      "news_sentiment": 78,
      "overall_reputation": 83,
      "platform_sentiment": {
        "twitter": 81,
        "reddit": 79,
        "customer_reviews": 86,
        "news_articles": 78
      },
      "service_quality": 84,
      "customer_experience": 86,
      "financial": 82,
      "value_proposition": 80,
      "trust_safety": 85,
      "pricing": "N/A",
      "rankings": "N/A"
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "sentiment_tracking_table": [
    ${cleanedBrands.map((brand, index) => `{
      "brand": "${brand}",
      "is_your_brand": ${index === 0},
      "service_quality": 84,
      "customer_experience": 86,
      "financial": 82,
      "value_proposition": 80,
      "trust_safety": 85
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "sentiment_trends": {
    "positive_aspects": [
      "Luxury Feel",
      "Service Experience",
      "Innovation"
    ],
    "negative_aspects": [
      "Pricing",
      "Maintenance Costs",
      "Competition"
    ],
    "neutral_aspects": [
      "Brand Recognition",
      "Market Presence",
      "Advertising"
    ]
  },
  "recommendations": [
    {
      "category": "sentiment_improvement",
      "issue": "High maintenance costs perceived negatively",
      "recommendation": "Consider offering more competitive maintenance packages",
      "priority": "High",
      "priority_score": 3,
      "expected_impact": "Increase in customer satisfaction and overall sentiment score",
      "target_score": 85,
      "brand": "${cleanedBrands[0]}"
    }
  ],
  "references": {
    "methodology": "AI-powered sentiment analysis using multiple data sources including social media, customer reviews, and news coverage",
    "sources": [
      {
        "title": "Search Results for: Customer reviews and ratings for ${cleanedBrands[0]}",
        "url": "https://duckduckgo.com/?q=Customer%20reviews%20and%20ratings%20for%20${cleanedBrands[0]}",
        "confidence": 70
      }
    ],
    "last_updated": "2025-10-10T00:00:00.000Z"
  },
  "metadata": {
    "response_time": 12.0,
    "brands_analyzed": ${cleanedBrands.length},
    "data_source": "AI Sentiment Analysis",
    "category": "${industry}",
    "your_brand": "${cleanedBrands[0]}",
    "competitors": [],
    "timestamp": "2025-01-01T00:00:00.000Z",
    "region": "${country}",
    "total_brands": ${cleanedBrands.length},
    "model_meta": {
      "model_used": "gpt-4o-mini",
      "analysis_engine": "sentiment_analysis_v1",
      "web_search_used": true
    }
  },
  "tab_name": "sentiment-analysis",
  "evidence_sources": [
    {
      "source": "DuckDuckGo",
      "url": "https://duckduckgo.com/?q=Customer%20reviews%20and%20ratings%20for%20${cleanedBrands[0]}",
      "title": "Search Results for: Customer reviews and ratings for ${cleanedBrands[0]}",
      "snippet": "Search results for Customer reviews and ratings for ${cleanedBrands[0]} from DuckDuckGo search engine with comprehensive information and resources.",
      "confidence": 0.7
    }
  ],
  "cached": false,
  "timestamp": "2025-01-01T00:00:00.000Z",
  "models_used": [
    "gpt-4o-mini"
  ],
  "plan_type": "generic_analysis"
}

IMPORTANT: Provide realistic sentiment scores based on your analysis of brand reputation and customer feedback.`;
  }

  /**
   * Build a prompt for AI Content Index analysis
   */
  static buildAIContentIndexPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global' } = params;
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');

    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert content analyst specializing in AI content strategy and digital presence analysis in the ${industry} industry.

Analyze AI content index for these brands${country === 'Global' ? ' globally' : ` in ${country}`}: ${brandsText}

Focus on:
1. Content domains and digital presence
2. Influence scores and citation metrics
3. Social media presence and engagement
4. Research impact and thought leadership
5. Industry collaborations and partnerships
6. Content quality and SEO optimization
7. User engagement and brand visibility

Provide your analysis in this exact JSON format:

{
  "brands": [
    ${cleanedBrands.map((brand, index) => `{
      "name": "${brand}",
      "content_domains": [
        {
          "domain": "Website Content",
          "influence_score": 85,
          "citation_count": 850,
          "social_media_presence": 80,
          "research_impact": 82,
          "industry_collaborations": 78,
          "content_quality": 88,
          "seo_optimization": 86,
          "user_engagement": 83,
          "brand_visibility": 87,
          "thought_leadership": 84
        },
        {
          "domain": "Social Media",
          "influence_score": 82,
          "citation_count": 920,
          "social_media_presence": 90,
          "research_impact": 75,
          "industry_collaborations": 80,
          "content_quality": 85,
          "seo_optimization": 78,
          "user_engagement": 88,
          "brand_visibility": 84,
          "thought_leadership": 80
        },
        {
          "domain": "Digital Media",
          "influence_score": 80,
          "citation_count": 780,
          "social_media_presence": 82,
          "research_impact": 78,
          "industry_collaborations": 85,
          "content_quality": 83,
          "seo_optimization": 81,
          "user_engagement": 80,
          "brand_visibility": 86,
          "thought_leadership": 82
        },
        {
          "domain": "YouTube Content",
          "influence_score": 78,
          "citation_count": 650,
          "social_media_presence": 85,
          "research_impact": 72,
          "industry_collaborations": 75,
          "content_quality": 80,
          "seo_optimization": 76,
          "user_engagement": 82,
          "brand_visibility": 79,
          "thought_leadership": 77
        },
        {
          "domain": "Thought Leadership",
          "influence_score": 88,
          "citation_count": 950,
          "social_media_presence": 80,
          "research_impact": 90,
          "industry_collaborations": 87,
          "content_quality": 89,
          "seo_optimization": 82,
          "user_engagement": 81,
          "brand_visibility": 88,
          "thought_leadership": 92
        }
      ],
      "overall_content_score": 85,
      "content_strengths": [
        "Strong website content quality",
        "Excellent SEO optimization",
        "High thought leadership presence"
      ],
      "content_weaknesses": [
        "Limited YouTube content engagement",
        "Underutilized industry collaborations"
      ],
      "content_opportunities": [
        "Enhance YouTube content strategy",
        "Expand industry partnerships"
      ],
      "key_insights": [
        "Strong digital content foundation",
        "Moderate social media engagement",
        "Potential for growth in video content"
      ]
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "chart_data": {
    "title": "AI Content Index Analysis",
    "data": [
      ${cleanedBrands.map((brand, index) => `{
        "name": "${brand}",
        "series": [
          {
            "name": "Website Content",
            "value": 85
          },
          {
            "name": "Social Media",
            "value": 82
          },
          {
            "name": "Digital Media",
            "value": 80
          },
          {
            "name": "YouTube Content",
            "value": 78
          },
          {
            "name": "Thought Leadership",
            "value": 88
          }
        ]
      }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n      ')}
    ]
  },
  "content_summary": {
    "overall_leader": {
      "brand": "${cleanedBrands[0]}",
      "average_score": 83,
      "strongest_domains": [
        "Thought Leadership",
        "Website Content",
        "Digital Media"
      ]
    },
    "domain_leaders": {
      "Website Content": "${cleanedBrands[0]}",
      "Social Media": "${cleanedBrands[0]}",
      "Digital Media": "${cleanedBrands[0]}",
      "YouTube Content": "${cleanedBrands[0]}",
      "Thought Leadership": "${cleanedBrands[0]}"
    },
    "content_distribution": {
      "excellent": 1,
      "good": 0,
      "average": 0,
      "below_average": 0
    }
  },
  "recommendations": [
    {
      "category": "content_improvement",
      "issue": "Limited YouTube content engagement",
      "recommendation": "Develop a targeted YouTube content strategy focusing on engaging ${industry.toLowerCase()} content and brand promotion.",
      "priority": "High",
      "priority_score": 3,
      "expected_impact": "Increase in user engagement and brand visibility",
      "target_score": 85,
      "brand": "${cleanedBrands[0]}"
    },
    {
      "category": "content_improvement",
      "issue": "Underutilized industry collaborations",
      "recommendation": "Strengthen existing partnerships and seek new collaborations to enhance brand impact and content distribution.",
      "priority": "Medium",
      "priority_score": 2,
      "expected_impact": "Improved brand impact and broader content reach",
      "target_score": 80,
      "brand": "General"
    }
  ],
  "context": {
    "title": "AI Content Index Analysis",
    "summary": "This analysis evaluates the digital content strategies and online presence of ${industry.toLowerCase()} brands, focusing on their website, social media, digital media, YouTube, and thought leadership.",
    "methodology": "The analysis was conducted using a combination of digital footprint assessment, social media analytics, website SEO evaluation, and brand visibility tracking.",
    "insights": [
      "Brands with strong SEO practices tend to have higher content visibility.",
      "Social media engagement is crucial for maintaining a dynamic digital presence.",
      "Thought leadership and brand influence significantly enhance overall content strategy effectiveness."
    ],
    "citations": []
  },
  "evidence_sources": [
    {
      "source": "${cleanedBrands[0]} Group",
      "url": "https://www.${brands[0].toLowerCase()}group.com/en/innovation/digitalization.html",
      "title": "${cleanedBrands[0]} Digital Content Strategy - Official Website",
      "snippet": "Official ${cleanedBrands[0]} Group website showcasing digital innovation, content strategy, and brand presence in the ${industry.toLowerCase()} industry.",
      "confidence": 0.9,
      "category": "digital_presence",
      "content_domain": "Website Content"
    },
    {
      "source": "LinkedIn",
      "url": "https://www.linkedin.com/company/${brands[0].toLowerCase()}-group/",
      "title": "${cleanedBrands[0]} Group LinkedIn - Social Media Presence",
      "snippet": "${cleanedBrands[0]} Group's official LinkedIn page showcasing social media presence, engagement, and digital content strategy for ${industry.toLowerCase()} innovation.",
      "confidence": 0.85,
      "category": "social_media",
      "content_domain": "Social Media"
    },
    {
      "source": "YouTube",
      "url": "https://www.youtube.com/@${cleanedBrands[0]}",
      "title": "${cleanedBrands[0]} YouTube Channel - Video Content",
      "snippet": "${cleanedBrands[0]}'s official YouTube channel featuring video content, brand information, and digital media strategy for ${industry.toLowerCase()} innovation.",
      "confidence": 0.85,
      "category": "video_content",
      "content_domain": "Youtube Content"
    }
  ],
  "metadata": {
    "analysis_type": "ai_content_index",
    "country": "${country}",
    "total_brands": ${cleanedBrands.length},
    "industry": "${industry}",
    "response_time": 15.0,
    "model_meta": {
      "model_used": "gpt-4o",
      "analysis_engine": "ai_content_index_v1",
      "web_search_used": true
    }
  },
  "timestamp": "2025-01-01T00:00:00.000Z",
  "tab_name": "ai-content-index"
}

IMPORTANT: Provide realistic content scores based on your analysis of ${industry.toLowerCase()} brand digital presence and content strategy.`;
  }


  // Helper methods for consistent data (cache-friendly)
  private static getColorForIndex(index: number): string {
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#00796b'];
    return colors[index % colors.length];
  }

  /**
   * Generate time periods from project creation date to current date
   * Ensures all months from project creation month to current month are included
   */
  private static generateTimePeriodsFromProjectDate(projectCreatedAt?: string): Array<{month: string, year: number}> {
    const timePeriods: Array<{month: string, year: number}> = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Always generate last 30 days from current date
    const endDate = new Date();
    
    // Generate 30 daily periods (last 30 days)
    for (let i = 29; i >= 0; i--) {
      const currentDate = new Date();
      currentDate.setDate(currentDate.getDate() - i);
      
      const day = currentDate.getDate();
      const monthName = months[currentDate.getMonth()];
      const year = currentDate.getFullYear();
      
      // Format: { month: 'Apr 06', year: 2024 } - zero-padded day
      const paddedDay = day < 10 ? `0${day}` : `${day}`;
      timePeriods.push({
        month: `${monthName} ${paddedDay}`,
        year: year
      });
    }
    
    return timePeriods;
  }

  /**
   * Build Performance Tracking prompt for brand visibility analysis
   */
  static buildPerformanceTrackingPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global', projectCreatedAt } = params;
    
    // Clean brand names to remove (YOU) suffix and get only the first brand (your brand)
    const cleanedBrands = this.cleanBrandNames(brands);
    const yourBrand = cleanedBrands[0] || 'Your Brand';

    // Generate time periods from project creation date to current date
    const timePeriods = this.generateTimePeriodsFromProjectDate(projectCreatedAt);

    const analysisPeriod = timePeriods.length > 0 
      ? `from ${timePeriods[0].month} ${timePeriods[0].year} to ${timePeriods[timePeriods.length - 1].month} ${timePeriods[timePeriods.length - 1].year}`
      : 'over the recent period';

    // Get current date for context
    const today = new Date();
    const currentDateStr = `${today.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][today.getMonth()]} ${today.getFullYear()}`;
    
    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert brand visibility analyst specializing in performance tracking and digital presence analysis in the ${industry} industry.

Analyze brand visibility performance for ${yourBrand}${country === 'Global' ? ' globally' : ` in ${country}`}.

CRITICAL DATE CONTEXT: Today's date is ${currentDateStr}. The analysis period ${analysisPeriod} represents HISTORICAL DATA from the PAST 30 DAYS. These are NOT future dates - they are dates that have already occurred. You MUST analyze historical brand visibility data for these past dates using the web search results provided.

CRITICAL: You MUST use the web search results provided to you to determine actual brand visibility scores for these HISTORICAL dates. The web search results contain real data from news articles, social media posts, search engine results, and platform mentions that occurred during these past 30 days. Do NOT create incremental or pattern-based values. Each time period's score must reflect real data from web search results for that specific historical day.

IMPORTANT: You MUST provide data for ALL time periods in the analysis range ${analysisPeriod} (these are HISTORICAL dates from the past 30 days, ending on ${currentDateStr}). The timeSeriesData array MUST contain entries for EACH of the following daily periods: ${timePeriods.map(p => `${p.month} ${p.year}`).join(', ')}. Do NOT skip any periods or only provide data for the last period.

Focus on:
1. Google Search visibility and SEO performance (daily tracking)
2. ChatGPT mentions and AI platform presence (daily metrics)
3. Social media buzz and engagement metrics (daily activity)
4. News coverage and media visibility (daily mentions)
5. AI platform presence and mentions (daily tracking)
6. Overall brand visibility across platforms (daily monitoring)
7. Daily time-series trends and performance tracking from ${timePeriods[0]?.month} ${timePeriods[0]?.year} to ${timePeriods[timePeriods.length - 1]?.month} ${timePeriods[timePeriods.length - 1]?.year}

MANDATORY REQUIREMENT FOR timeSeriesData: 
- These are HISTORICAL dates from the past 30 days (ending ${currentDateStr}), NOT future dates
- You MUST generate a data point for EACH of the ${timePeriods.length} daily time periods: ${timePeriods.map(p => `${p.month} ${p.year}`).join(', ')}
- Analyze web search results for EACH specific HISTORICAL day separately
- Provide realistic scores that reflect actual brand visibility trends for that specific past day
- Scores should vary based on real events, news coverage, product launches, marketing campaigns, and social media activity that occurred during each specific historical day
- Do NOT use incremental patterns (e.g., 75, 76, 77) - use data-driven values based on daily activity from web search results
- Each period's score must be based on actual web search results for that specific historical day
- The timeSeriesData array MUST have exactly ${timePeriods.length} entries, one for each daily period
- Use the web search results provided to extract real historical data for each date

Provide your analysis in this exact JSON format:

{
  "brands": [
    {
      "brand": "${yourBrand}",
      "google_search_visibility": 85,
      "chatgpt_mentions": 78,
      "social_media_buzz": 82,
      "news_coverage": 80,
      "ai_platform_presence": 83,
      "overall_score": 82
    }
  ],
  "timeSeriesData": [
    ${timePeriods.map((period, index) => {
      // Generate varied example scores (not incremental) to show the LLM it should use real daily data
      const exampleScores = [72, 85, 78, 91, 76, 88, 82, 79, 87, 74, 89, 83, 77, 84, 81, 86, 73, 90, 80, 75, 92, 85, 78, 88, 76, 82, 87, 79, 84, 81];
      const exampleScore = exampleScores[index % exampleScores.length];
      return `{
      "period": "${period.month} ${period.year}",
      "${yourBrand.toLowerCase()}": ${exampleScore}
    }`;
    }).join(',\n    ')}
  ],
  "platformBreakdown": [
    {
      "platform": "Google Search",
      "${yourBrand.toLowerCase()}": 85
    },
    {
      "platform": "ChatGPT",
      "${yourBrand.toLowerCase()}": 78
    },
    {
      "platform": "Social Media",
      "${yourBrand.toLowerCase()}": 82
    },
    {
      "platform": "News Coverage",
      "${yourBrand.toLowerCase()}": 80
    },
    {
      "platform": "AI Platforms",
      "${yourBrand.toLowerCase()}": 83
    },
    {
      "platform": "YouTube",
      "${yourBrand.toLowerCase()}": 76
    },
    {
      "platform": "LinkedIn",
      "${yourBrand.toLowerCase()}": 84
    }
  ],
  "context": {
    "title": "Brand Visibility Performance Tracking",
    "summary": "Comprehensive brand visibility analysis for ${yourBrand} across multiple platforms and time periods",
    "insights": [
      "${yourBrand} shows strong visibility across Google Search and Social Media platforms",
      "ChatGPT mentions and AI platform presence present growth opportunities",
      "News coverage shows room for improvement and optimization"
    ],
    "methodology": "Analysis based on brand visibility metrics across search engines, AI platforms, social media, news coverage, and professional networks"
  },
  "evidence_sources": [
    {
      "source": "${yourBrand} Official Website",
      "url": "https://www.${yourBrand.toLowerCase()}.com",
      "title": "${yourBrand} Official Brand Presence and Digital Strategy",
      "snippet": "Official information about ${yourBrand}'s digital presence and brand visibility strategy",
      "confidence": 0.9
    },
    {
      "source": "Social Media Analytics Report",
      "url": "https://example.com/social-analytics-${yourBrand.toLowerCase()}",
      "title": "${yourBrand} Social Media Presence and Engagement Analysis",
      "snippet": "Comprehensive analysis of ${yourBrand}'s social media visibility and engagement metrics",
      "confidence": 0.85
    },
    {
      "source": "Search Engine Analytics",
      "url": "https://example.com/search-analytics-${yourBrand.toLowerCase()}",
      "title": "${yourBrand} Google Search Visibility and SEO Performance",
      "snippet": "Analysis of ${yourBrand}'s search engine visibility and optimization performance",
      "confidence": 0.88
    }
  ],
  "recommendations": [
    {
      "category": "AI Platform Visibility",
      "priority": "High",
      "recommendation": "Increase presence on AI platforms and improve ChatGPT mentions through strategic content",
      "expected_impact": "Potential 15-20% increase in AI platform visibility"
    },
    {
      "category": "News Coverage Enhancement",
      "priority": "Medium",
      "recommendation": "Develop strategic PR initiatives and media relations to improve news coverage",
      "expected_impact": "Potential 10-15% improvement in news coverage metrics"
    },
    {
      "category": "Social Media Optimization",
      "priority": "High",
      "recommendation": "Optimize social media strategy to maintain and enhance current strong performance",
      "expected_impact": "Sustained leadership in social media visibility and engagement"
    }
  ],
  "metadata": {
    "response_time": 8.0,
    "brands_analyzed": 1,
    "data_source": "LLM + Web Search",
    "industry": "${industry}",
    "your_brand": "${yourBrand}",
    "competitors": [],
    "timestamp": "2025-01-01T00:00:00.000Z",
    "analysis_period": "${timePeriods[0]?.month || 'Jan'} ${timePeriods[0]?.year || 2025} - ${timePeriods[timePeriods.length - 1]?.month || 'Dec'} ${timePeriods[timePeriods.length - 1]?.year || 2025}",
    "project_created_at": "${projectCreatedAt || 'N/A'}",
    "analysis_start_date": "${timePeriods[0]?.month || 'Jan'} ${timePeriods[0]?.year || 2025}",
    "analysis_end_date": "${timePeriods[timePeriods.length - 1]?.month || 'Dec'} ${timePeriods[timePeriods.length - 1]?.year || 2025}"
  }
}

CRITICAL INSTRUCTIONS FOR DAILY TIME SERIES DATA:
- TODAY'S DATE: ${currentDateStr} - All dates below are HISTORICAL (past dates), NOT future dates
- You MUST provide data for ALL ${timePeriods.length} daily time periods: ${timePeriods.map(p => `${p.month} ${p.year}`).join(', ')} (these are past dates)
- The timeSeriesData array MUST contain exactly ${timePeriods.length} entries - one for each daily period listed above
- Analyze web search results SEPARATELY for each HISTORICAL daily time period (${timePeriods.map(p => `${p.month} ${p.year}`).join(', ')})
- Extract actual brand visibility metrics from news articles, social media data, search trends, and platform mentions that occurred during EACH specific historical day
- Calculate scores (0-100) based on real historical data for each day: search volume, news mentions, social engagement, platform presence
- Scores MUST vary realistically - they can increase, decrease, or stay stable based on actual events that occurred on that specific historical day
- DO NOT create incremental patterns (e.g., 75, 76, 77) - use data-driven values based on actual daily web search results
- DO NOT skip any periods - you must provide data for ALL ${timePeriods.length} daily periods: ${timePeriods.map(p => `${p.month} ${p.year}`).join(', ')}
- If web search shows increased activity on a specific historical day, the score for that day should reflect that
- If web search shows decreased activity on a specific historical day, the score for that day should reflect that
- Base each day's score on actual web search results for that specific historical day, not on mathematical patterns or trends
- REMEMBER: These are all PAST dates (last 30 days ending on ${currentDateStr}), use the web search results to find historical data for these dates

IMPORTANT: Provide realistic daily brand visibility scores based on your analysis of ${industry.toLowerCase()} brand digital presence and performance tracking using ACTUAL WEB SEARCH DATA for each specific HISTORICAL day. The web search results contain real historical information that you must use.`;
  }




  /**
   * Build a prompt for Consumer Insights analysis (Generic Brand Flow)
   * Simplified version to reduce API timeout issues
   */
  static buildConsumerInsightsPrompt(params: GenericPromptParams): string {
    const { brands, industry, country = 'Global' } = params;
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(brands);
    const brandsText = cleanedBrands.join(', ');

    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure. Do NOT return code blocks, markdown, or any explanations. Only return the JSON object.

You are an expert consumer behavior analyst specializing in brand perception and customer insights in the ${industry} industry.

Analyze consumer insights and customer behavior for these brands${country === 'Global' ? ' globally' : ` in ${country}`}: ${brandsText}

Focus on:
1. Customer satisfaction and experience metrics
2. Purchase decision factors and brand loyalty
3. Value perception and recommendation likelihood

Provide your analysis in this exact JSON format:

{
  "tab_name": "consumer-insights",
  "data": [
    ${cleanedBrands.map((brand, index) => `{
      "name": "${brand}${index === 0 ? ' (YOU)' : ''}",
      "series": [
        { "name": "Customer Satisfaction", "value": 85 },
        { "name": "Purchase Intent", "value": 78 },
        { "name": "Brand Loyalty", "value": 82 },
        { "name": "Value Perception", "value": 80 },
        { "name": "Brand Awareness", "value": 88 },
        { "name": "Recommendation Score", "value": 83 },
        { "name": "Customer Experience", "value": 86 },
        { "name": "Quality Perception", "value": 84 }
      ]
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "table": [
    ${cleanedBrands.map((brand, index) => `{
      "brand": "${brand}${index === 0 ? ' (YOU)' : ''}",
      "customer_satisfaction": 85,
      "brand_loyalty": 82,
      "recommendation_score": 83,
      "retention_rate": 78,
      "market_perception": 80,
      "customer_lifetime_value": 25000,
      "purchase_frequency": "Quarterly"
    }${index < cleanedBrands.length - 1 ? ',' : ''}`).join('\n    ')}
  ],
  "chart": {
    "title": "Consumer Insights Analysis - ${industry} Brands${country === 'Global' ? '' : ` in ${country}`}",
    "subtitle": "Customer Satisfaction, Purchase Intent, and Brand Loyalty Comparison",
    "chart_type": "radar",
    "x_axis": "Metrics",
    "y_axis": "Score (0-100)"
  },
  "evidence_sources": [
    {
      "category": "customer_satisfaction",
      "source": "Consumer Satisfaction Index",
      "confidence": 0.92,
      "url": "https://www.consumerindex.org/satisfaction",
      "snippet": "Industry-wide consumer satisfaction surveys measuring customer experience and service quality.",
      "type": "official_survey",
      "domain": "${industry.toLowerCase()}"
    },
    {
      "category": "brand_loyalty",
      "source": "Brand Loyalty Index",
      "confidence": 0.90,
      "url": "https://brandindex.com/loyalty",
      "snippet": "Analysis of customer retention, repeat purchase rates, and brand advocacy metrics.",
      "type": "industry_index",
      "domain": "${industry.toLowerCase()}"
    },
    {
      "category": "market_perception",
      "source": "Brand Perception Studies",
      "confidence": 0.87,
      "url": "https://brandstudies.org/perception",
      "snippet": "In-depth studies on brand image, positioning, and market perception among target consumers.",
      "type": "academic_research",
      "domain": "${industry.toLowerCase()}"
    }
  ],
  "recommendations": [
    {
      "category": "customer_experience",
      "priority": "High",
      "issue": "Customer experience optimization needed",
      "recommendation": "Enhance customer service and support infrastructure",
      "expected_impact": "Increase satisfaction by 10-15%"
    },
    {
      "category": "brand_loyalty",
      "priority": "High",
      "issue": "Retention improvement opportunity",
      "recommendation": "Implement loyalty programs and personalized engagement",
      "expected_impact": "Boost retention by 12-18%"
    }
  ],
  "comparative_insights": {
    "market_positioning": "${cleanedBrands[0]} positioned in premium segment with focus on quality and customer experience",
    "competitive_advantages": [
      "Strong brand recognition",
      "High customer satisfaction ratings",
      "Established market presence"
    ],
    "improvement_areas": [
      "Digital customer experience",
      "Brand differentiation",
      "Value communication"
    ]
  },
  "consumer_behavior_integration": {
    "purchase_analytics": {
      "total_customers": 250000,
      "active_customers": 180000
    },
    "customer_preferences": {
      "top_concerns": ["quality", "price", "customer_service"],
      "preferred_features": ["reliability", "innovation", "brand_reputation"]
    },
    "purchase_funnel": {
      "awareness_to_consideration": 75,
      "consideration_to_purchase": 68,
      "purchase_to_retention": 82
    }
  },
  "metadata": {
    "analysis_type": "consumer_insights",
    "country": "${country}",
    "total_brands": ${cleanedBrands.length},
    "industry": "${industry}",
    "timestamp": "2025-01-01T00:00:00.000Z"
  }
}

IMPORTANT: Provide realistic consumer insights with scores between 60-95 based on your analysis of brand performance, customer behavior, and market positioning in the ${industry} industry.`;
  }

  /**
   * Build Detailed Content Generation prompt for specific recommendation
   * Used by content generation popup to create ready-to-implement content
   */
  static buildDetailedContentGenerationPrompt(params: {
    brandName: string;
    industry: string;
    category: string;
    issue: string;
    currentScore: number;
  }): string {
    const { brandName, industry, category, issue, currentScore } = params;
    
    // Determine category-specific guidelines
    const categoryGuidelines = this.getCategoryContentGuidelines(category);
    
    return `IMPORTANT: You must respond with a valid JSON object that matches this exact structure.Do NOT return code blocks, markdown, or any explanations.Only return the JSON object.

You are a professional content strategist for ${ industry } brands.Create specific, actionable, ready - to - implement content that addresses the identified issue for ${ brandName }.

BRAND CONTEXT:
      --------------
        Brand Name: ${ brandName }
    Industry: ${ industry }
Content Category: ${ category }
Current Issue: ${ issue }
Current Score: ${ currentScore }/100

${ categoryGuidelines }

BRAND TERMINOLOGY REQUIREMENTS:
    --------------------------------
      Use ONLY brand - appropriate terminology throughout your content:
    - ✅ Use "brand" instead of "university"
      - ✅ Use "customer(s)" instead of "student(s)"
        - ✅ Use "potential customer(s)" instead of "prospective student(s)"
          - ✅ Use "product(s)/service(s)" instead of "course(s)/program(s)"
            - ✅ Use "team/experts" instead of "faculty"
              - ✅ Use "location/office/store" instead of "campus"
                - ✅ Use "customer acquisition" instead of "admission"
                  - ✅ Use "purchase/sign-up" instead of "enrollment"
                    - ✅ Use "past customers/brand advocates" instead of "alumni"
                      - ✅ Use "innovation/R&D" instead of "research"
                        - ✅ Use "professional/business" instead of "academic"
                          - ✅ Use "industry/market" instead of "higher education"

CONTENT GENERATION INSTRUCTIONS:
    ---------------------------------
      Generate comprehensive, ready - to - implement content in the following JSON format:

    {
      "content_overview": "Brief strategic description of the content approach and how it addresses the issue (2-3 sentences)",
        "implementation_timeline": "Specific timeline with phases (e.g., '2-3 weeks for content creation, 1 week for review and approval, 1 week for launch and promotion')",
          "content_pieces": [
            {
              "type": "Specific content type (e.g., 'landing page', 'social media post', 'blog article', 'video script', 'email campaign', 'infographic')",
              "title": "Compelling, specific, brand-focused title that grabs attention",
              "content": "Complete, ready-to-use content text (minimum 250-400 words for substantial pieces like articles/pages, 100-150 words for social media). Write actual content that ${brandName} can publish immediately. Be specific to ${brandName}, ${industry}, and the ${category} category. Include engaging headlines, clear value propositions, and strong storytelling.",
              "target_audience": "Specific target audience (e.g., 'potential customers aged 25-45 interested in premium ${industry.toLowerCase()} products', 'industry professionals and decision-makers', 'existing customers and brand advocates')",
              "platform": "Specific platform or channel (e.g., '${brandName} official website - ${category.toLowerCase()} section', 'LinkedIn Company Page', 'Instagram Feed', 'YouTube Channel', 'Email Newsletter')",
              "seo_keywords": ["keyword1", "keyword2", "keyword3", "keyword4", "keyword5"],
              "hashtags": ["#BrandRelevant", "#IndustryRelevant", "#CategoryRelevant", "#${brandName.replace(/\s+/g, '')}"],
              "call_to_action": "Specific, compelling CTA (e.g., 'Shop Now', 'Learn More', 'Contact Our Team', 'Download Guide', 'Schedule Consultation', 'Join Our Community')",
              "visual_suggestions": "Specific visual requirements and creative direction (e.g., 'Professional ${industry.toLowerCase()} product photography with lifestyle context', 'Infographic showing ${issue} solution', 'Customer testimonial video with authentic brand experience')"
            },
            {
              "type": "Second content type",
              "title": "Another compelling title",
              "content": "Another complete content piece...",
              "target_audience": "Specific audience",
              "platform": "Specific platform",
              "seo_keywords": ["keywords"],
              "hashtags": ["#hashtags"],
              "call_to_action": "Specific CTA",
              "visual_suggestions": "Visual requirements"
            },
            {
              "type": "Third content type",
              "title": "Third compelling title",
              "content": "Third complete content piece...",
              "target_audience": "Specific audience",
              "platform": "Specific platform",
              "seo_keywords": ["keywords"],
              "hashtags": ["#hashtags"],
              "call_to_action": "Specific CTA",
              "visual_suggestions": "Visual requirements"
            }
          ],
            "success_metrics": [
              "Specific, measurable metric with target numbers (e.g., 'Increase ${category.toLowerCase()} engagement by 35% within 3 months')",
              "Another specific metric (e.g., 'Improve ${brandName} brand visibility score from ${currentScore} to ${currentScore + 15} points')",
              "Third metric (e.g., 'Generate 500+ customer interactions within first month')",
              "Fourth metric (e.g., 'Achieve 25% increase in conversion rate for ${category.toLowerCase()}')"
            ],
              "next_steps": [
                "Specific, actionable step with owner (e.g., 'Review content with ${brandName} marketing team - Week 1')",
                "Another specific step (e.g., 'Set up analytics tracking for ${category} metrics - Week 1')",
                "Third step (e.g., 'Coordinate with design team for visual asset creation - Week 2')",
                "Fourth step (e.g., 'Schedule content publication across platforms - Week 3')",
                "Fifth step (e.g., 'Monitor performance and adjust strategy based on ${category} metrics - Ongoing')"
              ]
    }

QUALITY REQUIREMENTS:
    ---------------------
      1. Make content highly specific to ${ brandName } and the ${ industry } industry
    2. Address the specific issue: "${issue}"
    3. Generate at least 3 diverse content pieces covering different formats
    4. Each content piece should be 250 - 400 words minimum(100 - 150 for social media)
      5. Include actual content text that can be published immediately
    6. Be creative, engaging, and professional
    7. Focus on customer needs and brand value proposition
    8. Use strong storytelling and persuasive copywriting
    9. Include specific ${ brandName } brand elements and industry context
    10. Make all recommendations actionable and measurable

    IMPORTANT:
    - Generate 3 - 4 complete, ready - to - publish content pieces
      - Use only brand terminology(customers, products, services, not students, courses, programs)
        - Make content specific to ${ brandName }, not generic templates
          - Include actual compelling copy that demonstrates your understanding of ${ brandName } 's value in the ${industry} industry
            - Ensure all content addresses the issue: "${issue}"
              - Return ONLY the JSON object, no additional text or markdown formatting`;
  }

  /**
   * Get category-specific content guidelines for detailed generation
   */
  private static getCategoryContentGuidelines(category: string): string {
    const guidelines: { [key: string]: string } = {
      'Website Content': `
WEBSITE CONTENT GUIDELINES:
Create compelling website content pieces:
    - Landing pages with clear value propositions and conversion optimization
      - Product / service pages with detailed descriptions and customer benefits
        - About / Team pages showcasing expertise and brand story
          - Customer success stories and testimonials with real impact
            - Resource hubs with valuable industry insights and guides
              - Strong calls - to - action throughout(Buy Now, Contact Us, Learn More, Get Started)
                - SEO - optimized content with relevant industry keywords
                  - Scannable format with headings, bullet points, and visual breaks
                    - Mobile - responsive content structure`,

      'Social Media': `
SOCIAL MEDIA CONTENT GUIDELINES:
Create platform - specific, engaging social media content:
    - LinkedIn: Professional insights, industry leadership, company achievements, thought leadership
      - Instagram: Visual storytelling, behind - the - scenes, customer lifestyle, brand aesthetic
        - Facebook: Community engagement, customer stories, brand values, interactive content
          - Twitter / X: Real - time updates, industry conversations, quick tips, brand personality
            - TikTok: Creative, authentic content, trending formats, brand humanity
              - Include platform - appropriate hashtags for discoverability
                - Add interactive elements(polls, questions, calls for user content)
        - Create content series for consistency and audience building`,

      'Social Media Optimization': `
SOCIAL MEDIA OPTIMIZATION GUIDELINES:
Create optimized social media content:
    - Platform - specific content strategy(LinkedIn, Instagram, Facebook, Twitter, TikTok)
      - Engagement - focused posts with clear hooks and CTAs
        - Visual content with brand - consistent aesthetics
          - Community - building content(questions, polls, user - generated content)
            - Behind - the - scenes and authentic brand storytelling
              - Influencer collaboration opportunities
                - Trending topic integration with brand relevance
                  - Hashtag strategy for maximum reach
                    - Analytics - driven content optimization`,

      'YouTube Content': `
YOUTUBE CONTENT GUIDELINES:
Create engaging video content:
    - Product demonstration videos with clear value communication
      - Customer testimonial videos showcasing real experiences
        - How - to and tutorial videos solving customer problems
          - Behind - the - scenes content showing brand authenticity
            - Industry insight videos establishing thought leadership
              - Event coverage and brand milestone celebrations
                - Engaging video scripts with strong hooks(first 5 seconds)
                  - Clear calls - to - action(subscribe, visit website, shop now)
                    - SEO - optimized titles, descriptions, and tags
                      - Thumbnail strategy for maximum click - through`,

      'Digital Media': `
DIGITAL MEDIA CONTENT GUIDELINES:
Create comprehensive digital content:
    - Blog articles on industry trends, insights, and customer value
      - Email marketing campaigns for different customer segments
        - Digital advertising copy(Google Ads, Facebook Ads, LinkedIn Ads, Display)
          - Landing page content for specific campaigns and offers
            - Lead magnet content(guides, ebooks, webinars, toolkits)
              - Newsletter content with brand updates and value - added insights
                - Retargeting ad copy for customer journey optimization
                  - A / B testing variations for conversion optimization`,

      'Thought Leadership': `
THOUGHT LEADERSHIP CONTENT GUIDELINES:
Create authoritative thought leadership content:
    - Industry trend analysis and future predictions
      - Expert insights from brand leaders and team members
        - Original research findings and data - driven insights
          - White papers on relevant industry topics
            - Case studies showcasing problem - solving and innovation
              - Opinion pieces on industry challenges and opportunities
                - Speaking engagement topics and presentation materials
                  - Podcast or webinar content establishing expertise
                    - Industry report contributions and media commentary`,

      'AI Platform Visibility': `
AI PLATFORM VISIBILITY GUIDELINES:
Create content optimized for AI platforms:
      - Comprehensive brand information pages with clear entity data
        - FAQ content in natural language for AI understanding
          - Structured data markup for better AI interpretation
            - Authoritative content about brand expertise and offerings
              - Customer testimonials and social proof
                - Industry - specific terminology and semantic SEO
                  - Long - form content answering common queries
                    - Brand story and value proposition clarity
                      - Technical specifications and detailed product information
                        - Content optimized for featured snippets and knowledge graphs`,

      'News Coverage Enhancement': `
NEWS COVERAGE ENHANCEMENT GUIDELINES:
Create newsworthy content and press materials:
    - Press release content for brand announcements and milestones
      - Media kit materials with brand story and key facts
        - Expert commentary on industry news and trends
          - Original research and data worth media coverage
            - Partnership and collaboration announcements
              - Product launch narratives with compelling angles
                - Crisis communication and reputation management content
                  - Industry award submissions and recognition content
                    - Executive thought leadership for media quotes
                      - Community impact and corporate social responsibility stories`,

      'AI Content Index': `
AI CONTENT INDEX GUIDELINES:
Create content for improved AI indexing:
      - Comprehensive brand overview with clear positioning
        - Detailed product / service descriptions with specifications
        - Team expertise profiles with credentials
        - Customer success stories with quantifiable results
          - Industry insights and original perspectives
            - Technical content with proper structured data
              - FAQ sections addressing common queries
                - Brand differentiation and unique value propositions
                  - Historical brand information and milestones
                    - Content partnerships and authoritative backlinks`
    };

    return guidelines[category] || guidelines['Website Content'];
  }

  /**
   * Build a comprehensive prompt for content creation analysis
   */
  static buildContentCreationPrompt(params: GenericPromptParams): string {
    // Clean brand names to remove (YOU) suffix
    const cleanedBrands = this.cleanBrandNames(params.brands);
    const brands = cleanedBrands.join(', ');
    const industry = params.industry || 'Technology';
    const country = params.country || 'Global';
    
    return `You are a digital marketing expert analyzing content creation opportunities for ${ brands } in the ${ industry } industry(${ country } market).

Provide a comprehensive content creation analysis with the following structure:

## PLATFORM ANALYSIS

Analyze current content presence and identify improvement opportunities across these platforms:

### Social Media Platforms:
- ** Facebook **: Profile completeness, post frequency, engagement quality, customer testimonials
      - ** LinkedIn **: Professional presence, thought leadership content, industry connections
        - ** Instagram **: Visual storytelling, customer lifestyle content, brand aesthetic
          - ** Twitter **: Real - time engagement, industry conversations, brand voice
            - ** TikTok **: Creative content opportunities, trending participation, brand personality

### Website & Digital Presence:
- ** Homepage **: Value proposition clarity, call - to - action effectiveness, conversion optimization
      - ** Product Pages **: Detailed descriptions, customer reviews, usage examples, specifications
        - ** Team Profiles **: Expert credentials, personal stories, industry expertise showcase
          - ** Customer Stories **: Success stories, testimonials, case studies, user - generated content
            - ** Innovation Hub **: Research insights, development updates, thought leadership
              - ** Shop / Store **: Product presentation, purchasing experience, customer support

### Digital Content Strategy:
- ** Blog Posts **: Industry insights, educational content, SEO optimization
      - ** Video Content **: Product demos, behind - the - scenes, customer stories, tutorials
        - ** Infographics **: Data visualization, industry statistics, brand comparisons
          - ** Webinars **: Educational sessions, product launches, expert discussions
            - ** Podcasts **: Industry conversations, brand storytelling, expert interviews
              - ** E - books **: In - depth guides, whitepapers, industry reports

### Thought Leadership:
- ** Innovation Papers **: Research findings, industry insights, future predictions
      - ** Industry Reports **: Market analysis, trend reports, competitive intelligence
        - ** Expert Opinions **: Leadership insights, industry commentary, strategic guidance
          - ** Case Studies **: Success stories, problem - solving examples, implementation guides
            - ** White Papers **: Technical insights, methodology explanations, best practices

## CONTENT RECOMMENDATIONS

For each platform, provide specific recommendations including:

### Required Fields:
- ** content_type **: Platform - specific content type(e.g., "Social Media - Facebook", "Website - Homepage")
      - ** piece_type **: Specific content format(e.g., "social_post", "webpage", "video", "infographic")
        - ** title **: Compelling content title
          - ** keywords **: Relevant SEO and engagement keywords(array)
            - ** impact_score **: Estimated impact on brand visibility(0 - 100)
              - ** estimated_effort **: Time required for implementation(e.g., "1-2 weeks", "2-4 weeks")

### Platform Analysis Fields:
- ** current_status **: Current platform performance level
      - ** specific_issue **: Main problem or gap identified
        - ** improvement_needed **: Specific action required
          - ** priority **: Priority level(High / Medium / Low)
            - ** estimated_impact **: Expected improvement percentage
              - ** ready_to_generate **: Whether content can be generated immediately(true / false)

## BRAND TERMINOLOGY REQUIREMENTS

Use ONLY brand - appropriate terminology throughout:
    - ✅ "brand" instead of "university"
      - ✅ "customer" instead of "student"
        - ✅ "product/service" instead of "program"
          - ✅ "team/experts" instead of "faculty"
            - ✅ "onboarding/sign-up" instead of "admission"
              - ✅ "conversion" instead of "enrollment"
                - ✅ "market presence" instead of "academic presence"
                  - ✅ "customer acquisition" instead of "student recruitment"
                    - ✅ "brand reputation" instead of "academic reputation"

## RESPONSE FORMAT

Structure your response exactly as follows:

    \`\`\`json
{
  "brand": "${brands.split(',')[0]}",
  "platform_analysis": {
    "social_media": [
      {
        "platform": "Social Media",
        "sub_platform": "Facebook",
        "current_status": "Limited Engagement",
        "specific_issue": "Profile needs improvement - missing key product information and customer testimonials",
        "improvement_needed": "Update profile with product highlights, add customer success stories, and increase post frequency",
        "priority": "High",
        "estimated_impact": "Increase engagement by 40-60%",
        "ready_to_generate": true
      }
    ],
    "website": [...],
    "digital_content": [...],
    "thought_leadership": [...]
  },
  "content_recommendations": [
    {
      "content_type": "Social Media - Facebook",
      "piece_type": "social_post",
      "title": "Facebook Content Strategy for ${brands.split(',')[0]}",
      "keywords": ["facebook", "brand", "engagement", "community"],
      "impact_score": 85.0,
      "estimated_effort": "1-2 weeks"
    }
  ],
  "timestamp": "2025-10-10T00:00:00.000Z",
  "model_meta": {
    "model_used": "gpt-4o-mini",
    "search_engines": ["bing", "llm_web"],
    "data_freshness": "live"
  }
}
\`\`\`

Provide actionable, brand-focused insights that help ${brands} improve their digital content strategy and customer engagement across all platforms.`;
  }

  /**
   * Get the appropriate prompt based on tab type
   */
  static getPromptForTab(tabType: string, params: GenericPromptParams): string {
    switch (tabType) {
      case 'overview':
        return this.buildGenericOverviewPrompt(params);
      case 'ai-citations':
        return this.buildAICitationsPrompt(params);
      case 'category-analysis':
        return this.buildCategoryAnalysisPrompt(params);
      case 'sentiment-tracking':
        return this.buildSentimentTrackingPrompt(params);
      case 'ai-content-index':
        return this.buildAIContentIndexPrompt(params);
      case 'performance-tracking':
        return this.buildPerformanceTrackingPrompt(params);
      case 'content-creation':
        return this.buildContentCreationPrompt(params);
      case 'consumer-insights':
        return this.buildConsumerInsightsPrompt(params);
      default:
        return this.buildGenericOverviewPrompt(params);
    }
  }
}
